import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Form, Row, Col, Card, Tabs, Tab, Modal, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { 
    getAreas, createArea, updateArea, deleteArea, getCostCenters, createCostCenter, updateCostCenter, deleteCostCenter,
    getPassengers, createPassenger, updatePassenger, deletePassenger,
    getRequesters, createRequester, updateRequester, deleteRequester,
    bulkUploadPassengers,
    getWaitingRates, createWaitingRate, deleteWaitingRate, getVehicleTypes
} from '../api';
import AddressAutocomplete from './AddressAutocomplete';
import SearchableSelect from './SearchableSelect';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Upload, Users, FileSpreadsheet, Clock, Save, Trash2, Edit2, RotateCcw } from 'lucide-react';

const ClientStructureManager = ({ client, onBack }) => {
    const [key, setKey] = useState('personnel'); // Empezar en personal es más útil

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                <h5 className="mb-0">Gestión: {client.name}</h5>
                <Button variant="outline-secondary" size="sm" onClick={onBack}>Volver al Listado</Button>
            </Card.Header>
            <Card.Body>
                <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
                    <Tab eventKey="personnel" title="👥 Pasajeros y Solicitantes">
                        <PersonnelManager clientId={client.id} client={client} />
                    </Tab>
                    <Tab eventKey="waiting_rates" title="🕒 Tarifas de Espera">
                        <WaitingRateManager clientId={client.id} />
                    </Tab>
                    <Tab eventKey="areas" title="📂 Áreas">
                        <AreasManager clientId={client.id} />
                    </Tab>
                    <Tab eventKey="cost_centers" title="💰 Centros de Costo">
                        <CostCentersManager clientId={client.id} />
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>
    );
};

const WaitingRateManager = ({ clientId }) => {
    const [rates, setRates] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingRate, setEditingRate] = useState({
        vehicle_type_id: '',
        price_per_minute: 0,
        cost_per_minute: 0
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [ratesData, typesData] = await Promise.all([
                getWaitingRates(clientId),
                getVehicleTypes()
            ]);
            setRates(ratesData);
            setVehicleTypes(typesData);
        } catch (error) {
            toast.error("Error al cargar datos de espera.");
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSave = async () => {
        if (!editingRate.vehicle_type_id) {
            toast.warning("Seleccione un tipo de vehículo.");
            return;
        }
        try {
            await createWaitingRate({
                ...editingRate,
                client_id: clientId,
                vehicle_type_id: parseInt(editingRate.vehicle_type_id)
            });
            toast.success("Tarifa guardada.");
            setEditingRate({ vehicle_type_id: '', price_per_minute: 0, cost_per_minute: 0 });
            loadData();
        } catch (error) {
            toast.error("Error al guardar tarifa.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar esta tarifa?")) return;
        try {
            await deleteWaitingRate(id);
            toast.success("Tarifa eliminada.");
            loadData();
        } catch (error) {
            toast.error("Error al eliminar.");
        }
    };

    return (
        <div className="p-2">
            <Card className="mb-4 border-0 bg-light shadow-sm">
                <Card.Body>
                    <h6 className="mb-3 fw-bold text-primary"><Clock size={18} className="me-2"/>Configurar Nuevo Precio por Minuto</h6>
                    <Row className="align-items-end g-3">
                        <Col md={4}>
                            <Form.Label>Tipo de Vehículo</Form.Label>
                            <Form.Select 
                                value={editingRate.vehicle_type_id}
                                onChange={e => setEditingRate({...editingRate, vehicle_type_id: e.target.value})}
                            >
                                <option value="">Seleccione...</option>
                                {vehicleTypes.map(vt => (
                                    <option key={vt.id} value={vt.id}>{vt.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label>Venta al Cliente ($/min)</Form.Label>
                            <Form.Control 
                                type="number"
                                value={editingRate.price_per_minute}
                                onChange={e => setEditingRate({...editingRate, price_per_minute: parseFloat(e.target.value)})}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label>Costo Conductor ($/min)</Form.Label>
                            <Form.Control 
                                type="number"
                                value={editingRate.cost_per_minute || 0}
                                onChange={e => setEditingRate({...editingRate, cost_per_minute: parseFloat(e.target.value)})}
                            />
                        </Col>
                        <Col md={2}>
                            <Button variant="primary" className="w-100 fw-bold" onClick={handleSave}>
                                <Save size={16} className="me-2"/> Guardar
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Table responsive hover className="align-middle border shadow-sm rounded overflow-hidden">
                <thead className="bg-dark text-white">
                    <tr>
                        <th className="ps-3">Tipo Vehículo</th>
                        <th>Venta por Minuto</th>
                        <th>Costo por Minuto</th>
                        <th className="text-end pe-3">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {rates.map(r => (
                        <tr key={r.id}>
                            <td className="ps-3 fw-bold text-dark">{r.vehicle_type?.name || 'S/I'}</td>
                            <td><Badge bg="success" className="p-2 fs-6">${r.price_per_minute}</Badge></td>
                            <td><Badge bg="warning" className="p-2 fs-6 text-dark">${r.cost_per_minute || 0}</Badge></td>
                            <td className="text-end pe-3">
                                <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(r.id)}>
                                    <Trash2 size={18}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {rates.length === 0 && !loading && (
                        <tr><td colSpan="5" className="text-center py-4 text-muted small">No hay tarifas de espera configuradas para este cliente.</td></tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

const AreasManager = ({ clientId, onUpdate }) => {
    const [areas, setAreas] = useState([]);
    const [newArea, setNewArea] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [searchArea, setSearchArea] = useState('');

    const load = useCallback(async () => { 
        const data = await getAreas(clientId);
        setAreas(data); 
        if(onUpdate) onUpdate(data);
    }, [clientId, onUpdate]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const nameTrim = newArea.trim();
        if(!nameTrim) return;

        const exists = areas.some(a => (a.name || '').trim().toLowerCase() === nameTrim.toLowerCase() && a.is_active !== false);
        if (exists) {
            toast.warning(`El área "${nameTrim}" ya está registrada para este cliente.`);
            return;
        }

        try {
            await createArea({ name: nameTrim, client_id: clientId });
            setNewArea('');
            toast.success("Área creada con éxito.");
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Error al crear el área.";
            toast.error(msg);
        }
    };

    const handleStartEdit = (area) => {
        setEditingId(area.id);
        setEditingName(area.name);
    };

    const handleSaveEdit = async (id) => {
        if(!editingName.trim()) return;
        try {
            await updateArea(id, { name: editingName });
            setEditingId(null);
            load();
            toast.success("Área actualizada correctamente.");
        } catch (error) {
            toast.error("Error al actualizar área.");
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("¿Está seguro de que desea inactivar esta área?")) return;
        try {
            await deleteArea(id);
            load();
            toast.success("Área inactivada.");
        } catch (error) {
            toast.error("Error al inactivar área.");
        }
    };

    const handleReactivate = async (id) => {
        try {
            await updateArea(id, { is_active: true });
            load();
            toast.success("Área reactivada.");
        } catch (error) {
            toast.error("Error al reactivar área.");
        }
    };

    const filteredAreas = areas.filter(a => 
        a.name.toLowerCase().includes(searchArea.toLowerCase())
    );

    return (
        <div>
            <div className="d-flex gap-2 mb-3">
                <Form.Control 
                    placeholder="Nombre del Área (Ej. RRHH)" 
                    value={newArea} onChange={e => setNewArea(e.target.value)} 
                />
                <Button onClick={handleAdd}>Agregar</Button>
            </div>
            <div className="mb-3" style={{ maxWidth: '300px' }}>
                <Form.Control 
                    size="sm"
                    placeholder="🔍 Buscar área..." 
                    value={searchArea} 
                    onChange={e => setSearchArea(e.target.value)} 
                />
            </div>
            <Table size="sm" striped hover className="align-middle">
                <thead>
                    <tr>
                        <th>Nombre</th>
                        <th>Estado</th>
                        <th className="text-end pe-3" style={{ width: '150px' }}>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAreas.map(a => {
                        const isEditing = editingId === a.id;
                        const isActive = a.is_active !== false;
                        return (
                            <tr key={a.id} className={!isActive ? "text-muted" : ""} style={{ opacity: isActive ? 1 : 0.6 }}>
                                <td>
                                    {isEditing ? (
                                        <Form.Control 
                                            size="sm" 
                                            value={editingName} 
                                            onChange={e => setEditingName(e.target.value)} 
                                        />
                                    ) : (
                                        a.name
                                    )}
                                </td>
                                <td>
                                    {isActive ? (
                                        <Badge bg="success">Activo</Badge>
                                    ) : (
                                        <Badge bg="secondary">Inactivo</Badge>
                                    )}
                                </td>
                                <td className="text-end pe-3">
                                    {isEditing ? (
                                        <div className="d-flex gap-1 justify-content-end">
                                            <Button size="sm" variant="success" onClick={() => handleSaveEdit(a.id)}>Guardar</Button>
                                            <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancelar</Button>
                                        </div>
                                    ) : (
                                        <div className="d-flex gap-2 justify-content-end">
                                            {isActive && (
                                                <Button size="sm" variant="link" className="p-0 text-primary" onClick={() => handleStartEdit(a)} title="Editar">
                                                    <Edit2 size={16}/>
                                                </Button>
                                            )}
                                            {isActive ? (
                                                <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => handleDelete(a.id)} title="Inactivar">
                                                    <Trash2 size={16}/>
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="link" className="p-0 text-success" onClick={() => handleReactivate(a.id)} title="Reactivar">
                                                    <RotateCcw size={16}/>
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {filteredAreas.length === 0 && (
                        <tr>
                            <td colSpan="3" className="text-center py-3 text-muted small">No hay áreas registradas.</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

const CostCentersManager = ({ clientId, onUpdate }) => {
    const [ccs, setCcs] = useState([]);
    const [newCC, setNewCC] = useState({ code: '', name: '', file: '' });
    const [editingId, setEditingId] = useState(null);
    const [editingCode, setEditingCode] = useState('');
    const [editingName, setEditingName] = useState('');
    const [editingFile, setEditingFile] = useState('');
    const [searchCC, setSearchCC] = useState('');

    const load = useCallback(async () => { 
        const data = await getCostCenters(clientId);
        setCcs(data); 
        if(onUpdate) onUpdate(data);
    }, [clientId, onUpdate]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const codeTrim = (newCC.code || '').trim();
        if(!codeTrim) return;

        const exists = ccs.some(c => (c.code || '').trim().toLowerCase() === codeTrim.toLowerCase() && c.is_active !== false);
        if (exists) {
            toast.warning(`El Centro de Costo con código "${codeTrim}" ya está registrado para este cliente.`);
            return;
        }

        try {
            await createCostCenter({ ...newCC, code: codeTrim, client_id: clientId });
            setNewCC({ code: '', name: '', file: '' });
            toast.success("Centro de Costo creado con éxito.");
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Error al crear el Centro de Costo.";
            toast.error(msg);
        }
    };

    const handleStartEdit = (cc) => {
        setEditingId(cc.id);
        setEditingCode(cc.code);
        setEditingName(cc.name || '');
        setEditingFile(cc.file || '');
    };

    const handleSaveEdit = async (id) => {
        if(!editingCode.trim()) return;
        try {
            await updateCostCenter(id, { code: editingCode, name: editingName, file: editingFile });
            setEditingId(null);
            load();
            toast.success("Centro de costo actualizado correctamente.");
        } catch (error) {
            toast.error("Error al actualizar centro de costo.");
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("¿Está seguro de que desea inactivar este centro de costo?")) return;
        try {
            await deleteCostCenter(id);
            load();
            toast.success("Centro de costo inactivado.");
        } catch (error) {
            toast.error("Error al inactivar centro de costo.");
        }
    };

    const handleReactivate = async (id) => {
        try {
            await updateCostCenter(id, { is_active: true });
            load();
            toast.success("Centro de costo reactivado.");
        } catch (error) {
            toast.error("Error al reactivar centro de costo.");
        }
    };

    const filteredCCs = ccs.filter(c => 
        (c.code || '').toLowerCase().includes(searchCC.toLowerCase()) ||
        (c.name || '').toLowerCase().includes(searchCC.toLowerCase()) ||
        (c.file || '').toLowerCase().includes(searchCC.toLowerCase())
    );

    return (
        <div>
            <div className="d-flex gap-2 mb-3">
                <Form.Control 
                    placeholder="Código (Ej. CC-101)" 
                    value={newCC.code} onChange={e => setNewCC({...newCC, code: e.target.value})} 
                />
                 <Form.Control 
                    placeholder="Descripción (Opcional)" 
                    value={newCC.name} onChange={e => setNewCC({...newCC, name: e.target.value})} 
                />
                <Form.Control 
                    placeholder="File (Opcional)" 
                    value={newCC.file || ''} onChange={e => setNewCC({...newCC, file: e.target.value})} 
                />
                <Button onClick={handleAdd}>Agregar</Button>
            </div>
            <div className="mb-3" style={{ maxWidth: '300px' }}>
                <Form.Control 
                    size="sm"
                    placeholder="🔍 Buscar centro de costo..." 
                    value={searchCC} 
                    onChange={e => setSearchCC(e.target.value)} 
                />
            </div>
            <Table size="sm" striped hover className="align-middle">
                <thead>
                    <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>File</th>
                        <th>Estado</th>
                        <th className="text-end pe-3" style={{ width: '150px' }}>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredCCs.map(c => {
                        const isEditing = editingId === c.id;
                        const isActive = c.is_active !== false;
                        return (
                            <tr key={c.id} className={!isActive ? "text-muted" : ""} style={{ opacity: isActive ? 1 : 0.6 }}>
                                <td>
                                    {isEditing ? (
                                        <Form.Control 
                                            size="sm" 
                                            value={editingCode} 
                                            onChange={e => setEditingCode(e.target.value)} 
                                        />
                                    ) : (
                                        c.code
                                    )}
                                </td>
                                <td>
                                    {isEditing ? (
                                        <Form.Control 
                                            size="sm" 
                                            value={editingName} 
                                            onChange={e => setEditingName(e.target.value)} 
                                        />
                                    ) : (
                                        c.name || '-'
                                    )}
                                </td>
                                <td>
                                    {isEditing ? (
                                        <Form.Control 
                                            size="sm" 
                                            value={editingFile} 
                                            onChange={e => setEditingFile(e.target.value)} 
                                            placeholder="Pendiente..."
                                        />
                                    ) : (
                                        c.file || '-'
                                    )}
                                </td>
                                <td>
                                    {isActive ? (
                                        <Badge bg="success">Activo</Badge>
                                    ) : (
                                        <Badge bg="secondary">Inactivo</Badge>
                                    )}
                                </td>
                                <td className="text-end pe-3">
                                    {isEditing ? (
                                        <div className="d-flex gap-1 justify-content-end">
                                            <Button size="sm" variant="success" onClick={() => handleSaveEdit(c.id)}>Guardar</Button>
                                            <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancelar</Button>
                                        </div>
                                    ) : (
                                        <div className="d-flex gap-2 justify-content-end">
                                            {isActive && (
                                                <Button size="sm" variant="link" className="p-0 text-primary" onClick={() => handleStartEdit(c)} title="Editar">
                                                    <Edit2 size={16}/>
                                                </Button>
                                            )}
                                            {isActive ? (
                                                <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => handleDelete(c.id)} title="Inactivar">
                                                    <Trash2 size={16}/>
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="link" className="p-0 text-success" onClick={() => handleReactivate(c.id)} title="Reactivar">
                                                    <RotateCcw size={16}/>
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {filteredCCs.length === 0 && (
                        <tr>
                            <td colSpan="5" className="text-center py-3 text-muted small">No hay centros de costo registrados.</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

const PersonnelManager = ({ clientId, client }) => {
    const [passengers, setPassengers] = useState([]);
    const [requesters, setRequesters] = useState([]);
    const [areas, setAreas] = useState([]);
    const [ccs, setCcs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [searchPassenger, setSearchPassenger] = useState('');
    const [searchRequester, setSearchRequester] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('passenger');
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    const [showAreaModal, setShowAreaModal] = useState(false);
    const [showCCModal, setShowCCModal] = useState(false);

    const load = useCallback(async () => { 
        const [pax, req, areaList, ccList] = await Promise.all([
            getPassengers(clientId),
            getRequesters(clientId),
            getAreas(clientId),
            getCostCenters(clientId)
        ]);
        setPassengers(pax);
        setRequesters(req);
        setAreas(areaList);
        setCcs(ccList);
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    const openModal = (type, item = null) => {
        setModalType(type);
        setEditingItem(item);
        const clientEmailEnabled = client ? (client.send_email_notifications !== false) : true;
        if (item) {
            setFormData({
                full_name: item.full_name,
                email: item.email || '',
                phone: item.phone || '',
                home_address: item.home_address || '',
                office_address: item.office_address || '',
                area_id: item.area_id || '',
                cost_center_id: item.cost_center_id || '',
                give_access: item.has_portal_access || false,
                is_active: item.is_active !== undefined ? item.is_active : true,
                receive_emails: item.receive_emails !== undefined ? item.receive_emails : clientEmailEnabled,
                send_passenger_emails: item.send_passenger_emails !== undefined ? item.send_passenger_emails : clientEmailEnabled,
                send_custom_cc: item.send_custom_cc !== undefined ? item.send_custom_cc : false,
                custom_cc_email: item.custom_cc_email || '',
                password: ''
            });
        } else {
            setFormData({
                full_name: '', email: '', phone: '',
                home_address: '', office_address: '',
                area_id: '', cost_center_id: '',
                give_access: true,
                is_active: true,
                receive_emails: clientEmailEnabled,
                send_passenger_emails: clientEmailEnabled,
                send_custom_cc: false,
                custom_cc_email: '',
                password: ''
            });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                client_id: clientId,
                area_id: formData.area_id ? parseInt(formData.area_id) : null,
                cost_center_id: formData.cost_center_id ? parseInt(formData.cost_center_id) : null,
                has_portal_access: formData.give_access
            };

            delete payload.give_access;
            if (!payload.password) {
                delete payload.password;
            }

            if (modalType === 'passenger') {
                if (editingItem) await updatePassenger(editingItem.id, payload);
                else await createPassenger(payload);
            } else {
                if (editingItem) await updateRequester(editingItem.id, payload);
                else await createRequester(payload);
            }

            toast.success("Información guardada correctamente.");
            setShowModal(false);
            load();
        } catch (error) {
            console.error("Error saving profile:", error);
            toast.error("Error al guardar la información.");
        }
    };

    const handleDeleteItem = async () => {
        if (!editingItem) return;
        const confirmMsg = `¿Está seguro de que desea eliminar a este ${modalType === 'passenger' ? 'pasajero' : 'solicitante'}? Esta acción desactivará su cuenta de portal asociada y eliminará su registro.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            if (modalType === 'passenger') {
                await deletePassenger(editingItem.id);
            } else {
                await deleteRequester(editingItem.id);
            }
            toast.success("Eliminado correctamente.");
            setShowModal(false);
            load();
        } catch (error) {
            console.error("Error deleting profile:", error);
            toast.error("Error al eliminar.");
        }
    };

    const handleDownloadTemplate = () => {
        const exampleArea = areas.length > 0 ? areas[0].name : "RRHH";
        const exampleCC = ccs.length > 0 ? ccs[0].code : "CC-101";

        const data = [
            {
                "Nombre Completo": "Juan Pérez",
                "Email": "juan.perez@empresa.cl",
                "Teléfono": "+56912345678",
                "Área": exampleArea,
                "Centro de Costo": exampleCC,
                "Dirección Particular": "Calle Falsa 123, Santiago",
                "Dirección Oficina": "Av. Providencia 456, Providencia",
                "Tipo": "AMBOS",
                "Acceso Portal": "SI"
            },
            {
                "Nombre Completo": "María García",
                "Email": "maria@empresa.cl",
                "Teléfono": "",
                "Área": exampleArea,
                "Centro de Costo": exampleCC,
                "Dirección Particular": "",
                "Dirección Oficina": "",
                "Tipo": "PASAJERO",
                "Acceso Portal": "NO"
            }
        ];

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Plantilla Carga");

        const validValues = [
            ["ÁREAS VÁLIDAS", "CENTROS DE COSTO VÁLIDOS", "TIPOS DE PERFIL PERMITIDOS"],
            ...Array.from({ length: Math.max(areas.length, ccs.length, 3) }).map((_, i) => [
                areas[i]?.name || "",
                ccs[i]?.code || "",
                i === 0 ? "PASAJERO (Solo viaja)" : i === 1 ? "SOLICITANTE (Solo pide viajes)" : i === 2 ? "AMBOS (Viaja y pide viajes)" : ""
            ])
        ];
        const wsValid = XLSX.utils.aoa_to_sheet(validValues);
        XLSX.utils.book_append_sheet(wb, wsValid, "Valores Permitidos");

        XLSX.writeFile(wb, `Plantilla_Personal_Cliente_${clientId}.xlsx`);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    toast.error("El archivo está vacío.");
                    return;
                }

                const result = await bulkUploadPassengers(clientId, data);
                toast.success(result.message);
                load();
            } catch (err) {
                console.error("Bulk upload error:", err);
                const msg = err.response?.data?.detail || err.message || "Error al procesar el archivo.";
                alert(`Error en carga masiva:\n\n${msg}`);
            } finally {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const getAreaName = (id) => areas.find(a => a.id === id)?.name || '-';
    const getCCName = (id) => ccs.find(c => c.id === id)?.code || '-';

    const filteredRequesters = requesters.filter(r => 
        r.full_name.toLowerCase().includes(searchRequester.toLowerCase()) ||
        (r.email || '').toLowerCase().includes(searchRequester.toLowerCase()) ||
        (r.phone || '').toLowerCase().includes(searchRequester.toLowerCase())
    );

    const filteredPassengers = passengers.filter(p => 
        p.full_name.toLowerCase().includes(searchPassenger.toLowerCase()) ||
        (p.email || '').toLowerCase().includes(searchPassenger.toLowerCase()) ||
        (p.phone || '').toLowerCase().includes(searchPassenger.toLowerCase())
    );

    return (
        <div>
            <Row>
                <Col md={12} className="mb-4">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
                            <h6 className="mb-0 fw-bold"><Users size={16} className="me-2 text-success"/>Solicitantes</h6>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control 
                                    size="sm"
                                    placeholder="🔍 Buscar solicitante..." 
                                    value={searchRequester} 
                                    onChange={e => setSearchRequester(e.target.value)} 
                                    style={{ width: '220px' }}
                                />
                                <Button size="sm" variant="success" className="fw-bold" onClick={() => openModal('requester')}>+ Nuevo Solicitante</Button>
                            </div>
                        </Card.Header>
                        <Table size="sm" hover className="mb-0">
                            <thead className="bg-light"><tr><th className="ps-3">Nombre</th><th>Email/Tel</th><th>Imputación</th><th>Direcciones</th><th></th></tr></thead>
                            <tbody>
                                {filteredRequesters.map(r => (
                                    <tr key={r.id}>
                                        <td className="ps-3 fw-bold text-dark">
                                            {r.full_name}
                                            <div className="mt-1 d-flex gap-1">
                                                {r.is_active ? (
                                                    <Badge bg="success" style={{ fontSize: '9px' }}>Activo</Badge>
                                                ) : (
                                                    <Badge bg="secondary" style={{ fontSize: '9px' }}>Inactivo</Badge>
                                                )}
                                                {r.has_portal_access ? (
                                                    <Badge bg="info" style={{ fontSize: '9px' }}>Con Portal</Badge>
                                                ) : (
                                                    <Badge bg="light" text="dark" style={{ fontSize: '9px', border: '1px solid #ccc' }}>Sin Portal</Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td><div className="small">{r.email}</div><div className="small text-muted">{r.phone}</div></td>
                                        <td><small className="text-muted">Área: {getAreaName(r.area_id)}<br/>CC: {getCCName(r.cost_center_id)}</small></td>
                                        <td>
                                            {r.home_address && <div title="Casa" className="small text-muted">🏠 {r.home_address.substring(0,30)}...</div>}
                                            {r.office_address && <div title="Oficina" className="small text-muted">🏢 {r.office_address.substring(0,30)}...</div>}
                                        </td>
                                        <td className="text-end pe-3"><Button size="sm" variant="link" className="p-0" onClick={() => openModal('requester', r)}>✏️</Button></td>
                                    </tr>
                                ))}
                                {filteredRequesters.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-3 text-muted small">No se encontraron solicitantes.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card>
                </Col>

                <Col md={12}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <h6 className="mb-0 fw-bold"><Users size={16} className="me-2 text-primary"/>Pasajeros</h6>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-1 py-0 px-2 small" onClick={handleDownloadTemplate}>
                                        <FileSpreadsheet size={14}/> Plantilla
                                    </Button>
                                    <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1 py-0 px-2 small" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                        {uploading ? <Spinner animation="border" size="sm" /> : <Upload size={14}/>} 
                                        {uploading ? 'Cargando...' : 'Carga Masiva'}
                                    </Button>
                                    <input type="file" ref={fileInputRef} hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control 
                                    size="sm"
                                    placeholder="🔍 Buscar pasajero..." 
                                    value={searchPassenger} 
                                    onChange={e => setSearchPassenger(e.target.value)} 
                                    style={{ width: '220px' }}
                                />
                                <Button size="sm" variant="primary" className="fw-bold" onClick={() => openModal('passenger')}>+ Nuevo Pasajero</Button>
                            </div>
                        </Card.Header>
                        <Table size="sm" hover className="mb-0">
                            <thead className="bg-light"><tr><th className="ps-3">Nombre</th><th>Email/Tel</th><th>Imputación</th><th>Direcciones</th><th></th></tr></thead>
                            <tbody>
                                {filteredPassengers.map(p => (
                                    <tr key={p.id}>
                                        <td className="ps-3 fw-bold text-dark">
                                            {p.full_name}
                                            <div className="mt-1 d-flex gap-1">
                                                {p.is_active ? (
                                                    <Badge bg="success" style={{ fontSize: '9px' }}>Activo</Badge>
                                                ) : (
                                                    <Badge bg="secondary" style={{ fontSize: '9px' }}>Inactivo</Badge>
                                                )}
                                                {p.has_portal_access ? (
                                                    <Badge bg="info" style={{ fontSize: '9px' }}>Con Portal</Badge>
                                                ) : (
                                                    <Badge bg="light" text="dark" style={{ fontSize: '9px', border: '1px solid #ccc' }}>Sin Portal</Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td><div className="small">{p.email}</div><div className="small text-muted">{p.phone}</div></td>
                                        <td><small className="text-muted">Área: {getAreaName(p.area_id)}<br/>CC: {getCCName(p.cost_center_id)}</small></td>
                                        <td>
                                            {p.home_address && <div title="Casa" className="small text-muted">🏠 {p.home_address.substring(0,30)}...</div>}
                                            {p.office_address && <div title="Oficina" className="small text-muted">🏢 {p.office_address.substring(0,30)}...</div>}
                                        </td>
                                        <td className="text-end pe-3"><Button size="sm" variant="link" className="p-0" onClick={() => openModal('passenger', p)}>✏️</Button></td>
                                    </tr>
                                ))}
                                {filteredPassengers.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-3 text-muted small">No se encontraron pasajeros.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Form onSubmit={handleSave}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingItem ? 'Editar' : 'Nuevo'} {modalType === 'passenger' ? 'Pasajero' : 'Solicitante'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Nombre Completo *</Form.Label>
                                    <Form.Control required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mt-2">
                                <Form.Group>
                                    <Form.Label>Teléfono</Form.Label>
                                    <Form.Control value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+569..." />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mt-2 d-flex align-items-end">
                                <Form.Check 
                                    type="switch"
                                    id="active-switch"
                                    label={<strong>Perfil Activo</strong>}
                                    checked={formData.is_active}
                                    onChange={e => setFormData({...formData, is_active: e.target.checked})}
                                    className="ms-2 mb-2"
                                />
                            </Col>
                            <Col md={3} className="mt-2 d-flex align-items-end">
                                <Form.Check 
                                    type="switch"
                                    id="access-switch"
                                    label={<strong>Acceso Portal</strong>}
                                    checked={formData.give_access}
                                    onChange={e => setFormData({...formData, give_access: e.target.checked})}
                                    className="ms-2 mb-2"
                                />
                            </Col>
                            
                            {formData.give_access && (
                                <Col md={6} className="mt-3">
                                    <Form.Group>
                                        <Form.Label>Contraseña del Portal</Form.Label>
                                        <Form.Control 
                                            type="password" 
                                            value={formData.password || ''} 
                                            onChange={e => setFormData({...formData, password: e.target.value})} 
                                            placeholder={editingItem ? "Escriba para cambiar la clave..." : "Deje vacío para usar 'portal123'"}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        <h6 className="text-muted border-bottom pb-2 mt-4">Direcciones Frecuentes (Opcional)</h6>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Label>Casa</Form.Label>
                                <AddressAutocomplete 
                                    value={formData.home_address}
                                    onChange={val => setFormData({...formData, home_address: val})}
                                    onSelect={place => setFormData({...formData, home_address: place.address})}
                                    placeholder="Buscar dirección..."
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Oficina</Form.Label>
                                <AddressAutocomplete 
                                    value={formData.office_address}
                                    onChange={val => setFormData({...formData, office_address: val})}
                                    onSelect={place => setFormData({...formData, office_address: place.address})}
                                    placeholder="Buscar dirección..."
                                />
                            </Col>
                        </Row>

                        <h6 className="text-muted border-bottom pb-2 mt-4">Imputación por Defecto</h6>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Área</Form.Label>
                                    <InputGroup>
                                        <div className="flex-grow-1">
                                            <SearchableSelect
                                                options={areas
                                                    .filter(a => a.is_active !== false || String(a.id) === String(formData.area_id))
                                                    .map(a => ({
                                                        id: a.id,
                                                        name: a.name + (a.is_active === false ? " (Inactivo)" : "")
                                                    }))
                                                }
                                                value={formData.area_id}
                                                onChange={val => setFormData({...formData, area_id: val})}
                                                placeholder="Buscar Área..."
                                            />
                                        </div>
                                        {modalType === 'requester' && <Button variant="outline-secondary" onClick={() => setShowAreaModal(true)}>+</Button>}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Centro de Costo</Form.Label>
                                    <InputGroup>
                                        <div className="flex-grow-1">
                                            <SearchableSelect
                                                options={ccs
                                                    .filter(c => c.is_active !== false || String(c.id) === String(formData.cost_center_id))
                                                    .map(c => ({
                                                        id: c.id,
                                                        name: `${c.code}${c.name ? ` - ${c.name}` : ''}` + (c.is_active === false ? " (Inactivo)" : "")
                                                    }))
                                                }
                                                value={formData.cost_center_id}
                                                onChange={val => setFormData({...formData, cost_center_id: val})}
                                                placeholder="Buscar Centro de Costo..."
                                            />
                                        </div>
                                        {modalType === 'requester' && <Button variant="outline-secondary" onClick={() => setShowCCModal(true)}>+</Button>}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>

                        {modalType === 'requester' && (
                            <>
                                <h6 className="text-muted border-bottom pb-2 mt-4">Configuración de Notificaciones por Correo</h6>
                                <div className="bg-light p-3 rounded border mb-3">
                                    <Form.Check 
                                        type="checkbox"
                                        id="req-receive-emails"
                                        label="Recibir correos de confirmación y cancelación de reservas"
                                        checked={formData.receive_emails !== false}
                                        onChange={e => setFormData({ ...formData, receive_emails: e.target.checked })}
                                        className="mb-2 fw-semibold"
                                    />
                                    <Form.Check 
                                        type="checkbox"
                                        id="req-send-pax-emails"
                                        label="Enviar copia (CC) a los pasajeros asignados al viaje"
                                        checked={formData.send_passenger_emails !== false}
                                        onChange={e => setFormData({ ...formData, send_passenger_emails: e.target.checked })}
                                        className="mb-2 fw-semibold"
                                    />
                                    <Form.Check 
                                        type="checkbox"
                                        id="req-send-custom-cc"
                                        label="Enviar copia adicional (CC) a otra(s) casilla(s) de correo"
                                        checked={formData.send_custom_cc === true}
                                        onChange={e => setFormData({ ...formData, send_custom_cc: e.target.checked })}
                                        className="mb-2 fw-semibold"
                                    />
                                    {formData.send_custom_cc && (
                                        <div className="ps-4 mt-2">
                                            <Form.Label className="small fw-semibold text-secondary mb-1">Correo(s) CC adicional(es)</Form.Label>
                                            <Form.Control 
                                                type="text"
                                                size="sm"
                                                placeholder="ej: finanzas@empresa.com, asistente@empresa.com"
                                                value={formData.custom_cc_email || ''}
                                                onChange={e => setFormData({ ...formData, custom_cc_email: e.target.value })}
                                            />
                                            <Form.Text className="text-muted small d-block mt-1">
                                                💡 Si deseas agregar más de un correo, sepáralos con coma (,).
                                            </Form.Text>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                        <div>
                            {editingItem && (
                                <Button variant="danger" className="fw-bold" onClick={handleDeleteItem}>
                                    Eliminar Perfil
                                </Button>
                            )}
                        </div>
                        <div>
                            <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>Cancelar</Button>
                            <Button variant="primary" type="submit">Guardar</Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showAreaModal} onHide={() => setShowAreaModal(false)} size="sm">
                <Modal.Header closeButton><Modal.Title>Nueva Área</Modal.Title></Modal.Header>
                <Modal.Body>
                    <AreasManager clientId={clientId} onUpdate={(data) => { setAreas(data); }} />
                </Modal.Body>
            </Modal>

            <Modal show={showCCModal} onHide={() => setShowCCModal(false)} size="sm">
                <Modal.Header closeButton><Modal.Title>Nuevo Centro Costo</Modal.Title></Modal.Header>
                <Modal.Body>
                    <CostCentersManager clientId={clientId} onUpdate={(data) => { setCcs(data); }} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ClientStructureManager;
